import { useEffect } from "react";

import { InnerPageChrome } from "@/components/layout/InnerPageChrome";
import type { DynamicPageKind } from "@/data/dynamicPages";
import { useDynamicDetail } from "@/data/dynamicPages";
import { useVorinAssets } from "@/pages/shared/useVorinAssets";
import { MINIMALISTIC_PORTFOLIO_STYLESHEETS } from "@/pages/shared/vorinDemoStyles";
import { localizeHref } from "@/i18n/locale";
import { uiText } from "@/i18n/translations";

import styles from "./DynamicPages.module.scss";

type DynamicDetailPageProps = {
  kind: DynamicPageKind;
  slug: string;
  currentPath: string;
};

export function DynamicDetailPage({ kind, slug, currentPath }: DynamicDetailPageProps) {
  useVorinAssets({ extraStylesheets: MINIMALISTIC_PORTFOLIO_STYLESHEETS });
  const { data, error } = useDynamicDetail(kind, slug);

  useEffect(() => {
    if (!data) return;
    document.title = `${data.seoTitle || data.title} | VenueX`;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", data.searchDescription || data.summary);
  }, [data]);

  if (error) {
    return (
      <InnerPageChrome currentPath={currentPath}>
        <main className={styles.statePage}>
          <span>VenueX</span>
          <h1>{uiText("Page unavailable.")}</h1>
          <p>{error}</p>
          <a href={localizeHref(`/${kind === "service" ? "service" : kind}/`)}>{uiText("Back to archive")}</a>
        </main>
      </InnerPageChrome>
    );
  }

  if (!data) {
    return (
      <InnerPageChrome currentPath={currentPath}>
        <main className={styles.loadingPage} aria-label={uiText("Loading page")}>
          <span></span><span></span><span></span>
        </main>
      </InnerPageChrome>
    );
  }

  return (
    <InnerPageChrome currentPath={currentPath}>
      <article className={styles.page}>
        <header className={styles.detailHero}>
          <a className={styles.backLink} href={data.links.back.href}>
            <span aria-hidden="true">&#8592;</span> {data.links.back.label}
          </a>
          <span className={styles.eyebrow}>{data.eyebrow}</span>
          <h1>{data.title}</h1>
          {data.meta.length > 0 && <p className={styles.meta}>{data.meta.join(" / ")}</p>}
        </header>

        {data.imageUrl && (
          <div className={styles.detailImage}>
            <img
              src={data.imageUrl}
              alt={data.imageAlt}
              style={{ objectPosition: data.imagePosition }}
            />
          </div>
        )}

        <section className={styles.detailContent}>
          <p className={styles.detailLead}>{data.summary}</p>
          {data.bodyHtml && (
            <div
              className={styles.richText}
              dangerouslySetInnerHTML={{ __html: data.bodyHtml }}
            />
          )}
        </section>

        <section className={styles.detailCta}>
          <a className={styles.primaryLink} href={data.links.primary.href}>
            {data.links.primary.label} <span aria-hidden="true">&#8594;</span>
          </a>
          {data.links.secondary.label && (
            <a className={styles.secondaryLink} href={data.links.secondary.href}>
              {data.links.secondary.label}
            </a>
          )}
        </section>
      </article>
    </InnerPageChrome>
  );
}
